import React from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  Chip,
  Typography,
  Stack,
  Alert,
  CircularProgress,
  Box,
  Divider,
} from "@mui/material";
import { useProductCoverage } from "../../hooks/useDistributionCoverage";
import { distributionService } from "../../services/distributionService";
import type { PurchaseSuggestion } from "../../services/distributionService";

export interface StoreCoverageDialogProps {
  unifiedProductId: string | null;
  productName?: string;
  open: boolean;
  onClose: () => void;
}

export function StoreCoverageDialog({
  unifiedProductId,
  productName,
  open,
  onClose,
}: StoreCoverageDialogProps): React.ReactElement {
  const { data: coverage, isLoading, isError } = useProductCoverage(
    unifiedProductId,
    open && Boolean(unifiedProductId),
  );

  const [suggestion, setSuggestion] = React.useState<
    PurchaseSuggestion | null
  >(null);
  const [loadingSuggestion, setLoadingSuggestion] = React.useState(false);

  React.useEffect(() => {
    if (!open || !unifiedProductId) {
      setSuggestion(null);
      return;
    }
    let cancelled = false;
    setLoadingSuggestion(true);
    distributionService
      .validatePurchaseCoverage(unifiedProductId, 0)
      .then((result) => {
        if (!cancelled) setSuggestion(result.suggestion);
      })
      .catch(() => {
        if (!cancelled) setSuggestion(null);
      })
      .finally(() => {
        if (!cancelled) setLoadingSuggestion(false);
      });
    return () => {
      cancelled = true;
    };
  }, [open, unifiedProductId]);

  const totalStores = coverage
    ? coverage.stores_covered.length + coverage.stores_uncovered.length
    : 0;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        Cobertura por tienda
        {productName && (
          <Typography variant="body2" color="text.secondary">
            {productName}
          </Typography>
        )}
        <Typography variant="caption" color="text.secondary" display="block">
          Producto: {unifiedProductId}
        </Typography>
      </DialogTitle>

      <DialogContent dividers>
        {isLoading && (
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              minHeight: 200,
            }}
          >
            <CircularProgress />
          </Box>
        )}

        {isError && (
          <Alert severity="error">
            No se pudo obtener la cobertura. Verifica que el producto tenga
            ABC clasificación, store_profile poblado y forecast por tienda.
          </Alert>
        )}

        {coverage && !isLoading && (
          <Stack spacing={2}>
            <Stack direction="row" spacing={2} flexWrap="wrap">
              <Chip
                label={`${coverage.stores_covered.length} / ${totalStores} tiendas cubiertas`}
                color={
                  coverage.stores_uncovered.length === 0 ? "success" : "warning"
                }
              />
              <Chip
                label={`${coverage.pending_denials} denegaciones pendientes`}
                color={coverage.pending_denials > 5 ? "error" : "default"}
              />
              <Chip
                label={`Compra propuesta: ${coverage.total_qty_planned.toFixed(0)} u`}
                variant="outlined"
              />
            </Stack>

            <Divider />

            <Typography variant="subtitle2">Desglose por fuente</Typography>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Fuente</TableCell>
                  <TableCell align="right">Cantidad</TableCell>
                  <TableCell>Detalle</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                <TableRow>
                  <TableCell>Reposición</TableCell>
                  <TableCell align="right">
                    {coverage.qty_replenishment_total.toFixed(0)}
                  </TableCell>
                  <TableCell>tiendas con historial bajo ROP</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell>Muestra</TableCell>
                  <TableCell align="right">
                    {coverage.qty_sample_total.toFixed(0)}
                  </TableCell>
                  <TableCell>tiendas candidatas seeding</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell>Reorder post-muestra</TableCell>
                  <TableCell align="right">
                    {coverage.qty_reorder_total.toFixed(0)}
                  </TableCell>
                  <TableCell>muestras success agotadas</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell>Cumplir denegaciones</TableCell>
                  <TableCell align="right">
                    {coverage.qty_denial_fulfillment_total.toFixed(0)}
                  </TableCell>
                  <TableCell>{coverage.pending_denials} denials abiertos</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell>Buffer central</TableCell>
                  <TableCell align="right">
                    {coverage.qty_central_buffer.toFixed(0)}
                  </TableCell>
                  <TableCell>cobertura mínima en bodega</TableCell>
                </TableRow>
              </TableBody>
            </Table>

            <Divider />

            <Typography variant="subtitle2">
              Cobertura por tienda
              {loadingSuggestion && (
                <CircularProgress size={14} sx={{ ml: 1 }} />
              )}
            </Typography>

            {suggestion ? (
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Tienda</TableCell>
                    <TableCell>Razón déficit</TableCell>
                    <TableCell align="right">Stock actual</TableCell>
                    <TableCell align="right">ROP</TableCell>
                    <TableCell align="right">Reposición</TableCell>
                    <TableCell align="right">Muestra</TableCell>
                    <TableCell align="right">Denial</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {suggestion.breakdown_by_store.map((line) => {
                    const totalStoreQty =
                      line.qty_replenishment +
                      line.qty_sample +
                      line.qty_reorder +
                      line.qty_denial_fulfillment;
                    if (totalStoreQty === 0) return null;
                    return (
                      <TableRow key={line.store}>
                        <TableCell>
                          <strong>{line.store}</strong>
                        </TableCell>
                        <TableCell>
                          <Chip
                            size="small"
                            label={line.deficit_reason}
                            color={
                              line.deficit_reason === "denial"
                                ? "error"
                                : line.deficit_reason === "replenishment"
                                  ? "warning"
                                  : "default"
                            }
                          />
                        </TableCell>
                        <TableCell align="right">
                          {line.current_stock.toFixed(0)}
                        </TableCell>
                        <TableCell align="right">
                          {line.rop.toFixed(1)}
                        </TableCell>
                        <TableCell align="right">
                          {line.qty_replenishment > 0
                            ? line.qty_replenishment.toFixed(0)
                            : "—"}
                        </TableCell>
                        <TableCell align="right">
                          {line.qty_sample > 0
                            ? line.qty_sample.toFixed(0)
                            : "—"}
                        </TableCell>
                        <TableCell align="right">
                          {line.qty_denial_fulfillment > 0
                            ? line.qty_denial_fulfillment.toFixed(0)
                            : "—"}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            ) : (
              !loadingSuggestion && (
                <Alert severity="info">
                  No hay desglose por tienda calculable. Posibles causas:
                  product_inventory aún no conectado, falta forecast por
                  tienda, o store_profile sin poblar.
                </Alert>
              )
            )}

            {coverage.stores_uncovered.length > 0 && (
              <Alert severity="warning">
                Tiendas sin cobertura tras esta compra:{" "}
                {coverage.stores_uncovered.join(", ")}.
              </Alert>
            )}
          </Stack>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose}>Cerrar</Button>
      </DialogActions>
    </Dialog>
  );
}
